import { useDeleteAdminsUserId, getGetAdminsUserIdQueryKey } from '@/api/generated/admin/admin'
import { useDeleteProvidersUserId, getGetProvidersUserIdQueryKey } from '@/api/generated/doctor/doctor'
import { useQueryClient } from '@tanstack/react-query'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { DELETE_MEMBER_ERROR } from '@/store/member/MemberConstants'
import { userManagementTypes } from '../Constants'
import { getMemberApiErrorPayload } from './getMemberApiErrorPayload'

/**
 * Deletes a clinic member (admin or provider).
 *
 * Replaces the `deleteMember` thunk + DELETE_MEMBER* reducer flow. The old flow set a
 * `deleteSuccess` flag that member.jsx watched to navigate back to the management page;
 * the caller now passes that navigation as `onSuccess`. On failure the existing
 * DELETE_MEMBER_ERROR reducer case is preserved by dispatching it with the API error body,
 * so the error-message UX is unchanged.
 */
export function useDeleteMember(userId: string, userType: string) {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const isAdmin = userType === userManagementTypes.ADMIN
  const deleteAdmin = useDeleteAdminsUserId()
  const deleteProvider = useDeleteProvidersUserId()
  const activeMutation = isAdmin ? deleteAdmin : deleteProvider

  function mutate(opts?: { onSuccess?: () => void; onError?: (error: unknown) => void }) {
    activeMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: isAdmin ? getGetAdminsUserIdQueryKey(userId) : getGetProvidersUserIdQueryKey(userId),
          })
          opts?.onSuccess?.()
        },
        onError: (error) => {
          dispatch({ type: DELETE_MEMBER_ERROR, payload: getMemberApiErrorPayload(error), failed: true })
          opts?.onError?.(error)
        },
      },
    )
  }

  return { mutate, isPending: activeMutation.isPending }
}
